import { useEffect, useState } from 'react';
import { api } from '../api/client';

/**
 * "This site's connector went backwards."
 *
 * A version regression is never routine. It means either a deploy overwrote the plugin with an older
 * committed copy — an ordinary consequence of keeping `wp-content` in version control, and what
 * happened to a real site on 2026-08-03 — or somebody downgraded the agent deliberately. The
 * platform cannot distinguish those, so this states what was observed and lets the operator decide.
 *
 * Shown, not alerted. A routine deploy can trigger it, and paging on every occurrence teaches people
 * to dismiss it — a dismissed alert is worse than none. Same posture as the edge health marker and
 * `env:check`: report, never block.
 *
 * The reason it is worth surfacing at all: silently downgrading a security agent is a standard early
 * step in an intrusion, and a downgrade re-opens every vulnerability fixed in between. Before this,
 * the new version simply replaced the old one and the transition left no trace anywhere.
 */
export function ConnectorDowngrade({ siteId }: { siteId: string }) {
  const [events, setEvents] = useState<{ at: string; from: string; to: string }[]>([]);

  useEffect(() => {
    let live = true;
    api
      .auditEvents()
      .then(({ events: all }) => {
        if (!live) return;
        setEvents(
          all
            .filter((e) => e.type === 'site.connector_downgraded')
            .filter((e) => (e.detail as { siteId?: string } | undefined)?.siteId === siteId)
            .map((e) => {
              const d = e.detail as { from?: string; to?: string } | undefined;
              return { at: e.occurredAt, from: d?.from ?? '?', to: d?.to ?? '?' };
            }),
        );
      })
      // A missing audit trail must not look like a clean bill of health, but it also must not break
      // the page. Staying silent is the honest middle: this component claims nothing it cannot read.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [siteId]);

  if (events.length === 0) return null;
  const latest = events[0];
  if (!latest) return null;

  return (
    <div className="row">
      <span className="label">Connector</span>
      <span>
        <span className="badge badge-warn">Downgraded</span>{' '}
        <span className="mono">
          {latest.from} → {latest.to}
        </span>{' '}
        <span className="muted">at {latest.at}</span>
        <p className="muted">
          This site reported an older connector than it previously ran. A site deploy can overwrite
          the plugin with an older committed copy — check whether the version in your repository
          matches the one you expect. If no deploy explains it, treat it as a change worth
          investigating: a downgrade re-opens anything fixed in between.
          {events.length > 1 ? ` Seen ${events.length} times.` : ''}
        </p>
      </span>
    </div>
  );
}
